import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { BsClock } from "react-icons/bs";
import { VscListFlat } from "react-icons/vsc";
import { GiStarShuriken } from "react-icons/gi";
import { MdQuiz } from "react-icons/md";
import { checkAuthAndRedirect } from "../../../utils/auth";
import { fetchAllTracksForCourses } from "../../../utils/trackUtils";
import {
  fetchCoursesByTrack,
  fetchSubjectsOfCourses,
  type Course,
  type CourseSubjectOption,
} from "../../../utils/courseUtils";
import {
  fetchSubjectsForTrack,
  fetchTopicsForSubject,
} from "../../../utils/contentUtils";
import TestFilterSelect from "./TestFilterSelect";
import TestCardSchedule from "./TestCardSchedule";
import type { TestData } from "./testTypes";
import {
  fetchTestSubjects,
  fetchTestsList,
  formatDate,
  formatTime,
  getVisibleTestSearchText,
  matchesVisibleCardSearch,
} from "./testUtils";
import {
  saveAssignTestContext,
  saveConfigureDetailContext,
} from "./testRouteUtils";
import { useTestCardSearchQuery } from "./TestPageShell";
import { useTestRoutes } from "./testRoutes";

const COURSE_ALL = "__all__";

const metaIconStyle: React.CSSProperties = {
  display: "inline-block",
  fontSize: "1.2em",
  lineHeight: 1,
  verticalAlign: "middle",
  flexShrink: 0,
};

/**
 * Configure module list view: browse tests by track/course/subject/topic filters.
 */
const TestAssignList: React.FC = () => {
  const navigate = useNavigate();
  const cardSearchQuery = useTestCardSearchQuery();
  const routes = useTestRoutes();

  const [assignTracks, setAssignTracks] = useState<
    Awaited<ReturnType<typeof fetchAllTracksForCourses>>
  >([]);
  const [assignTracksLoading, setAssignTracksLoading] = useState(false);
  const [assignCourses, setAssignCourses] = useState<Course[]>([]);
  const [assignCoursesLoading, setAssignCoursesLoading] = useState(false);
  const [assignSubjects, setAssignSubjects] = useState<CourseSubjectOption[]>(
    []
  );
  const [assignSubjectsLoading, setAssignSubjectsLoading] = useState(false);
  const [assignTopics, setAssignTopics] = useState<
    Awaited<ReturnType<typeof fetchTopicsForSubject>>
  >([]);
  const [assignTopicsLoading, setAssignTopicsLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");

  const [tests, setTests] = useState<TestData[]>([]);
  const [loading, setLoading] = useState(false);
  const [openingTestId, setOpeningTestId] = useState<string | null>(null);

  const buildAssignFilter = useCallback(
    () => ({
      track: selectedTrack,
      subject: selectedSubject,
      topic: selectedTopic,
    }),
    [selectedTrack, selectedSubject, selectedTopic]
  );

  const refreshTests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTestsList(buildAssignFilter());
      setTests(data);
    } catch (error) {
      console.error("Error fetching tests:", error);
      setTests([]);
    } finally {
      setLoading(false);
    }
  }, [buildAssignFilter]);

  useEffect(() => {
    if (!checkAuthAndRedirect()) return;
    let cancelled = false;
    (async () => {
      setAssignTracksLoading(true);
      try {
        const tracks = await fetchAllTracksForCourses();
        if (!cancelled) setAssignTracks(tracks);
      } catch (err) {
        console.error("Failed to fetch tracks for configure:", err);
        if (!cancelled) setAssignTracks([]);
      } finally {
        if (!cancelled) setAssignTracksLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedTrack) {
      setAssignCourses([]);
      setSelectedCourse("");
      setAssignSubjects([]);
      setAssignTopics([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setAssignCoursesLoading(true);
      try {
        const courses = await fetchCoursesByTrack(selectedTrack);
        if (!cancelled) {
          setAssignCourses(courses);
          setSelectedCourse(COURSE_ALL);
          setSelectedSubject("");
          setSelectedTopic("");
          setAssignTopics([]);
        }
      } catch (err) {
        console.error("Failed to fetch courses for configure:", err);
        if (!cancelled) {
          setAssignCourses([]);
          setSelectedCourse("");
        }
      } finally {
        if (!cancelled) setAssignCoursesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTrack]);

  useEffect(() => {
    if (!selectedTrack || !selectedCourse) {
      setAssignSubjects([]);
      setAssignTopics([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setAssignSubjectsLoading(true);
      try {
        const subjects =
          selectedCourse === COURSE_ALL
            ? await fetchSubjectsForTrack(selectedTrack, {
                excludeProject: true,
              })
            : await fetchSubjectsOfCourses([selectedCourse]);
        if (!cancelled) {
          setAssignSubjects(subjects);
          setAssignTopics([]);
        }
      } catch (err) {
        console.error("Failed to fetch subjects:", err);
        if (!cancelled) setAssignSubjects([]);
      } finally {
        if (!cancelled) setAssignSubjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTrack, selectedCourse]);

  useEffect(() => {
    if (!selectedSubject) {
      setAssignTopics([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setAssignTopicsLoading(true);
      try {
        const topics = await fetchTopicsForSubject(selectedSubject);
        if (!cancelled) setAssignTopics(topics);
      } catch (err) {
        console.error("Failed to fetch topics:", err);
        if (!cancelled) setAssignTopics([]);
      } finally {
        if (!cancelled) setAssignTopicsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSubject]);

  useEffect(() => {
    refreshTests();
  }, [refreshTests]);

  const filteredTests = tests.filter((test) =>
    matchesVisibleCardSearch(getVisibleTestSearchText(test), cardSearchQuery)
  );

  const handleCardClick = async (test: TestData) => {
    if (openingTestId) return;
    setOpeningTestId(test.test_id);
    try {
      const saved = await fetchTestSubjects(test.test_id);
      const subjects =
        saved.subjects.length > 0
          ? saved.subjects
          : (test.subject_ids || []).map((id, index) => ({
              subject_id: id,
              subject_name: test.subjects?.[index] || id,
            }));
      const testWithContext: TestData = {
        ...test,
        track_id: saved.track_id ?? test.track_id,
        subjects: subjects.map((row) => row.subject_name),
        subject_ids: subjects.map((row) => row.subject_id),
      };

      saveAssignTestContext(testWithContext);
      saveConfigureDetailContext({
        test_id: testWithContext.test_id,
        subjects,
      });
      navigate(
        routes.configureDetail(testWithContext.title, testWithContext.test_id),
        {
          state: {
            test: testWithContext,
            subjects,
            batchConfigs: [],
          },
        }
      );
    } catch (error) {
      console.error("Failed to open configure detail:", error);
    } finally {
      setOpeningTestId(null);
    }
  };

  return (
    <>
      <div className="d-flex rounded-1 justify-content-start py-2 gap-2">
        <div
          className="col-3 filterSection flex-shrink-0"
          style={{
            height: "calc(100vh - 150px)",
            overflowY: "auto",
            minWidth: "200px",
          }}
        >
          <div
            className="border m-0 border-light p-3 px-4"
            style={{ boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
          >
            <div className="d-flex flex-column gap-3">
              <TestFilterSelect
                label="Track"
                value={selectedTrack}
                onChange={(value) => {
                  setSelectedTrack(value);
                  setSelectedCourse("");
                  setSelectedSubject("");
                  setSelectedTopic("");
                }}
                disabled={assignTracksLoading}
                placeholder="Select Track"
                options={assignTracks.map((track) => ({
                  value: track.track_id,
                  label: track.track_name,
                }))}
                fullWidth
              />
              <TestFilterSelect
                label="Course"
                value={selectedCourse}
                onChange={(value) => {
                  setSelectedCourse(value);
                  setSelectedSubject("");
                  setSelectedTopic("");
                }}
                disabled={
                  !selectedTrack ||
                  assignCoursesLoading ||
                  assignCourses.length === 0
                }
                placeholder="Select Course"
                options={[
                  { value: COURSE_ALL, label: "All" },
                  ...assignCourses.map((course) => ({
                    value: course.course_id,
                    label: course.course_name,
                  })),
                ]}
                fullWidth
              />
              <TestFilterSelect
                label="Subject"
                value={selectedSubject}
                onChange={(value) => {
                  setSelectedSubject(value);
                  setSelectedTopic("");
                }}
                disabled={
                  !selectedTrack ||
                  !selectedCourse ||
                  assignSubjectsLoading ||
                  assignSubjects.length === 0
                }
                placeholder="Select Subject"
                options={assignSubjects.map((subject) => ({
                  value: subject.subject_id,
                  label: subject.subject_name,
                }))}
                fullWidth
              />
              <TestFilterSelect
                label="Topic"
                value={selectedTopic}
                onChange={setSelectedTopic}
                disabled={
                  !selectedSubject ||
                  assignTopicsLoading ||
                  assignTopics.length === 0
                }
                placeholder="Select Topic"
                options={assignTopics.map((topic) => ({
                  value: topic.topic_id,
                  label: topic.topic_name,
                }))}
                fullWidth
              />
            </div>
          </div>
        </div>
        <div
          className={`col filteredDataDisplaySection flex-grow-1 overflow-y-auto${loading ? " overflow-hidden" : ""}`}
          style={{
            height: "calc(100vh - 150px)",
            overflowX: "hidden",
            paddingTop: "2px",
          }}
        >
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : filteredTests.length > 0 ? (
            filteredTests.map((test) => (
              <div
                key={test.test_id}
                role="button"
                tabIndex={0}
                className="mb-2 ms-2 p-2 bg-white rounded border border-light shadow-sm test-card-clickable"
                style={{ cursor: "pointer" }}
                onClick={() => handleCardClick(test)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCardClick(test);
                  }
                }}
              >
                <div className="d-flex align-items-start justify-content-between gap-2 pb-1 border-bottom">
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-semibold mb-0 lh-sm">
                      {test.title || "Test Title"}
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 mt-1 text-secondary">
                      <span className="d-inline-flex align-items-center">
                        <VscListFlat className="me-1" />
                        {test.subjects?.length
                          ? test.subjects.join(", ")
                          : test.subject || "N/A"}
                      </span>
                      <span className="d-inline-flex align-items-center">
                        <BsClock className="me-1" style={metaIconStyle} />
                        {test.duration}{" "}
                        {test.duration === "1" ? "Minute" : "Minutes"}
                      </span>
                      <span className="d-inline-flex align-items-center">
                        <GiStarShuriken className="me-1" />
                        {test.marks} Marks
                      </span>
                      {test.test_type && (
                        <span className="d-inline-flex align-items-center">
                          <MdQuiz className="me-1" />
                          {test.test_type}
                        </span>
                      )}
                    </div>
                  </div>
                  {(test.date || test.time) && (
                    <TestCardSchedule
                      dateLabel={test.date ? formatDate(test.date) : null}
                      timeLabel={test.time ? formatTime(test.time) : null}
                    />
                  )}
                </div>

                <div className="d-flex align-items-end gap-2 pt-1">
                  <div className="flex-grow-1 overflow-hidden">
                    {test.batch && (
                      <div className="fs-6 mb-1">
                        <span className="fw-semibold">Batch:</span> {test.batch}
                      </div>
                    )}
                    <div>
                      <span className="fw-semibold">Description</span>
                      <div className="text-dark lh-sm mt-1 text-break">
                        {test.description || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : tests.length > 0 && cardSearchQuery ? (
            <p className="text-muted ms-3 py-3 text-center">
              No tests match your search.
            </p>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default TestAssignList;
